import { dispatch } from "../../state/store";
import { setErrorCharts, setLoadedChart } from "../../state/ducks";

import { rumEvents } from "../../consts";
import { httpClient, log, notificationService } from "../../services";
import { fetchEventsConfig, fetchMarketEventsConfig } from "./fetchConfigs";

const apiUrl = process.env.REACT_APP_API_URL;

const getParamsUrl = (
  url,
  {
    eventType,
    startTime,
    endTime,
    market,
    store,
    deviceFilterPattern,
    osName,
    browserName,
    appType,
    connectionType
  },
  nextToken
) => {
  let urlParams = `eventType=${eventType}&startTime=${startTime}&endTime=${endTime}`;

  if (market) {
    urlParams += `&market=${market}`;
  }

  if (store) {
    urlParams += `&store=${store}`;
  }

  if (osName) {
    urlParams += `&osName=${osName}`;
  }

  if (browserName) {
    urlParams += `&browserName=${browserName}`;
  }

  if (appType) {
    urlParams += `&appType=${appType}`;
  }

  if (connectionType) {
    urlParams += `&connectionType=${connectionType}`;
  }

  if (deviceFilterPattern) {
    const dFP = Buffer.from(deviceFilterPattern).toString("base64");

    urlParams += `&deviceFilterPattern=${dFP}`;
  }

  if (nextToken) {
    urlParams += `&nextToken=${nextToken}`;
  }

  return `${url}?${urlParams}`;
};

const getRequestData = async () => {
  return `${apiUrl}/monitoring/get-monitoring-events`;
};

const fetchEventsBatch = async (url) => {
  const data = await httpClient.get(url);

  return data?.data;
};

const fetchEvents = async (params) => {
  const url = await getRequestData();

  let hasToken = true;
  let tokenUrl = getParamsUrl(url, params);
  const data = [];

  const eventConfig = params.eventConfig ?? {};

  if (!eventConfig) {
    return null;
  }

  while (hasToken) {
    const res = await fetchEventsBatch(tokenUrl);

    const nextToken = res?.nextToken;

    if (res?.events) {
      data.push(...res.events);
    }

    if (nextToken) {
      tokenUrl = getParamsUrl(url, params, nextToken);
    } else {
      hasToken = false;
    }
  }

  if (data && eventConfig?.saveDataFn) {
    // Save Events in the global state
    eventConfig.saveDataFn(data);
  }
  return data;
};

const fetchEventsSequentially = async (eventsFetchRequests, index) => {
  if (eventsFetchRequests && eventsFetchRequests?.length > index) {
    const requestConfig = eventsFetchRequests[index];
    const requestGroup = requestConfig?.eventConfig?.group;
    const chartNames = requestConfig?.eventConfig?.chartNames;

    try {
      await fetchEvents(requestConfig);
      setLoaded(chartNames);
    } catch (err) {
      setLoaded(chartNames);
      setErrors(chartNames);

      log.error(`rumEventsService: Error fetching ${requestGroup} events`, err);
      notificationService.showError({
        title: `Error fetching ${requestGroup} events`,
        message: err?.toString().includes("Network Error") ? "Network error" : err?.toString()
      });
    }

    const newIndex = index + 1;

    if (eventsFetchRequests?.length > newIndex) {
      return fetchEventsSequentially(eventsFetchRequests, newIndex);
    }
  }
};

const fetchEventsList = async ({ eventNames, isMarketEvents, params, deviceName }) => {
  const eventsFetchRequests = [];

  eventNames.forEach(async (eventType) => {
    const eventConfig = isMarketEvents
      ? fetchMarketEventsConfig[eventType] ?? {}
      : fetchEventsConfig[eventType] ?? {};

    const filteredParams = params;

    if (eventType === rumEvents.PERFORMANCE_NAVIGATION_EVENT) {
      filteredParams.connectionType = null;
    }

    if (eventConfig) {
      eventsFetchRequests.push({
        eventType,
        isMarketEvents,
        ...filteredParams,
        eventConfig,
        deviceFilterPattern:
          eventConfig?.deviceFilterPatternFn && eventConfig.deviceFilterPatternFn(deviceName)
      });
    }
  });

  // return Promise.all(eventsFetchRequests);
  return await fetchEventsSequentially(eventsFetchRequests, 0);
};

const setLoaded = (chartNames) => {
  chartNames &&
    chartNames?.forEach((chart) => {
      dispatch(setLoadedChart(chart));
    });
};

const setErrors = (chartNames) => {
  if (chartNames && chartNames?.length) {
    dispatch(setErrorCharts(chartNames));
  }
};

const rumEventsService = {
  fetchEventsList
};

export default rumEventsService;
